import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap, shareReplay } from 'rxjs/operators';
import { environment } from '../../config/editor-environment';
import { SessionService } from '../session.service';
import { EmbeddedAuthPayload } from '@infinitform/platform-core';
import { EMBEDDED_AUTH_TIMEOUT_MS, EmbeddedBridgeService } from '../embedded/embedded-bridge.service';

/**
 * A Copilot model the signed-in user may choose. Resolved server-side and
 * returned with the token — the client neither decides eligibility nor ever
 * sees a model id, so the picker can render this list verbatim.
 */
export interface AiModelOption {
  key: string;
  label: string;
  description: string;
  internal: boolean;
}

/** Always available; the fallback when the server sent no list. */
export const DEFAULT_AI_MODEL_KEY = 'default';

export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  requiresTwoFactor?: boolean;
  preAuthToken?: string;
  email?: string;
  role?: string;
  permissions?: string[];
  message?: string;
  // Subscription features
  manufacturing_features?: string[];
  cad_generation_features?: string[];
  platform_features?: string[];
  load_case_features?: string[];
  constraints_features?: string[];
  cad_download_features?: string[];
  plugin_features?: string[];
  push_pull_features?: PushPullFeatures;
  subscription_status?: string;
  ai_model_options?: AiModelOption[];
  process_comparison_enabled?: boolean;
}

export interface TwoFactorVerificationResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  email: string;
  role: string;
  permissions: string[];
  message?: string;
}

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

/**
 * Manufacturing method features that can be restricted by subscription
 */
export enum ManufacturingMethodFeatures {
  ADDITIVE_MANUFACTURING = 'AdditiveManufacturing',
  DIE_CASTING = 'DieCasting',
  EXTRUSION = 'Extrusion',
  INJECTION_MOLDING_WITH_DRAW_DIRECTION = 'InjectionMoldingWithDrawDirection',
  INJECTION_MOLDING_WITH_EXTRUSION = 'InjectionMoldingWithExtrusion',
  INVESTMENT_CASTING = 'InvestmentCasting',
  MILLING = 'Milling'
}

/**
 * CAD generation features that can be restricted by subscription
 */
export enum CadGenerationFeatures {
  ORGANIC = 'Organic',
  PRISMATIC = 'Prismatic'
}

/**
 * Platform features that can be restricted by subscription
 */
export enum PlatformFeatures {
  AI_CHAT = 'AiChat'
}

/**
 * Shown wherever Copilot is hidden or disabled because of the subscription
 * tier. Cloud Standard does not include AI — Advanced and Professional do.
 * Single constant so the icon tooltip, the toasts and the chat panel all say
 * the same thing.
 */
export const COPILOT_UNAVAILABLE_MESSAGE =
  'Copilot is available in the Advanced and Professional tiers';

/**
 * Load case features that can be restricted by subscription
 */
export enum LoadCaseFeatures {
  STATIC = 'Static',
  VIBRATION = 'Vibration',
  DYNAMIC = 'Dynamic'
}

/**
 * Constraints features that can be restricted by subscription
 */
export enum ConstraintsFeatures {
  COMPLIANCE = 'Compliance',
  STRESS = 'Stress',
  DEFORMATION = 'Deformation',
  FREQUENCY = 'Frequency'
}

/**
 * CAD download features that can be restricted by subscription
 */
export enum CadDownloadFeatures {
  ORGANIC = 'Organic',
  PRISMATIC = 'Prismatic'
}

export enum PluginFeatures {
  NATIVE_PARAMETRIC = 'NativeParametric'
}

/**
 * Maps ManufacturingMethod enum values to ManufacturingMethodFeatures
 */
export const MANUFACTURING_METHOD_TO_FEATURE: Record<number, ManufacturingMethodFeatures> = {
  0: ManufacturingMethodFeatures.ADDITIVE_MANUFACTURING,  // AdditiveManufacturing
  1: ManufacturingMethodFeatures.MILLING,                 // Milling
  2: ManufacturingMethodFeatures.MILLING,                 // MillingNew
  3: ManufacturingMethodFeatures.MILLING,                 // Milling2
  4: ManufacturingMethodFeatures.INJECTION_MOLDING_WITH_DRAW_DIRECTION, // InjectionMoldingWithDrawDirection
  5: ManufacturingMethodFeatures.DIE_CASTING,             // DieCasting
  6: ManufacturingMethodFeatures.EXTRUSION,               // Extrusion
  7: ManufacturingMethodFeatures.INJECTION_MOLDING_WITH_EXTRUSION, // InjectionMoldingWithExtrusion
  8: ManufacturingMethodFeatures.INVESTMENT_CASTING       // InvestmentCasting
};

/**
 * Subscription features available to the user
 */
export interface SubscriptionFeatures {
  manufacturing_features: string[];
  cad_generation_features: string[];
  platform_features: string[];
  load_case_features: string[];
  constraints_features: string[];
  cad_download_features: string[];
  plugin_features: string[];
  push_pull_features: PushPullFeatures;
  subscription_status: string;
  subscription_seat_active?: boolean;
}

export const DEFAULT_AI_MODEL_OPTION: AiModelOption = {
  key: DEFAULT_AI_MODEL_KEY,
  label: 'Standard',
  description: 'The production Copilot model.',
  internal: false
};

export interface PushPullFeatures {
  organic: boolean;
  prismatic: boolean;
  native_parametric: boolean;
}

/**
 * All features granted when subscriptions are disabled
 */
const ALL_FEATURES: SubscriptionFeatures = {
  manufacturing_features: Object.values(ManufacturingMethodFeatures),
  cad_generation_features: Object.values(CadGenerationFeatures),
  platform_features: Object.values(PlatformFeatures),
  load_case_features: Object.values(LoadCaseFeatures),
  constraints_features: Object.values(ConstraintsFeatures),
  cad_download_features: Object.values(CadDownloadFeatures),
  plugin_features: Object.values(PluginFeatures),
  push_pull_features: {
    organic: true,
    prismatic: true,
    native_parametric: true
  },
  subscription_status: 'active',
  subscription_seat_active: true
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private sessionService = inject(SessionService);
  private embeddedBridge = inject(EmbeddedBridgeService);

  // Cache the refresh token observable to prevent multiple simultaneous calls
  private refreshToken$: Observable<any> | null = null;

  // In-memory token storage
  private accessToken: string | null = null;
  private tokenExpiry: number | null = null;
  private email: string | null = null;
  private userRole: string | null = null;

  // Copilot models offered to this user, from the token response.
  private _aiModelOptions: AiModelOption[] = [];

  private _processComparisonEnabled = false;

  // Subscription features from token
  private _subscriptionFeatures: SubscriptionFeatures = {
    manufacturing_features: [],
    cad_generation_features: [],
    platform_features: [],
    load_case_features: [],
    constraints_features: [],
    cad_download_features: [],
    plugin_features: [],
    push_pull_features: {
      organic: false,
      prismatic: false,
      native_parametric: false
    },
    subscription_status: 'none',
    subscription_seat_active: true
  };

  // Debug override for subscription features (only used when SUBSCRIPTION_DEBUG is enabled)
  private _debugOverride: SubscriptionFeatures | null = null;

  /**
   * Login with email and password
   */
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        `${environment.API_ADDRESS}/auth/login`,
        { email, password },
        { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
      )
      .pipe(
        map((response) => {
          // Store tokens if login is successful and no 2FA required
          if (response.access_token && !response.requiresTwoFactor) {
            this.storeTokens(response);
          }
          return response;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Verify 2FA token
   */
  verifyTwoFactor(preAuthToken: string, token: string): Observable<TwoFactorVerificationResponse> {
    return this.http
      .post<TwoFactorVerificationResponse>(
        `${environment.API_ADDRESS}/auth/verify-2fa`,
        { preAuthToken, token },
        { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
      )
      .pipe(
        map((response) => {
          this.storeTokens(response);
          return response;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Verify backup code
   */
  verifyBackupCode(preAuthToken: string, code: string): Observable<TwoFactorVerificationResponse> {
    return this.http
      .post<TwoFactorVerificationResponse>(
        `${environment.API_ADDRESS}/auth/verify-backup-code`,
        { preAuthToken, code },
        { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
      )
      .pipe(
        map((response) => {
          this.storeTokens(response);
          return response;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Store authentication tokens and subscription features
   */
  private storeTokens(response: any): void {
    if (response.access_token) {
      this.accessToken = response.access_token;
      const expiresIn = response.expires_in || 86400; // Default 24 hours
      // Calculate expiry time with 30-second buffer for network delays
      this.tokenExpiry = Date.now() + (expiresIn - 30) * 1000;
      this.email = response.email;
      this.userRole = response.role || null;

      // Store subscription features
      this._subscriptionFeatures = {
        manufacturing_features: response.manufacturing_features || [],
        cad_generation_features: response.cad_generation_features || [],
        platform_features: response.platform_features || [],
        load_case_features: response.load_case_features || [],
        constraints_features: response.constraints_features || [],
        cad_download_features: response.cad_download_features || [],
        plugin_features: response.plugin_features || [],
        push_pull_features: response.push_pull_features || {
          organic: false,
          prismatic: false,
          native_parametric: false
        },
        subscription_status: response.subscription_status || 'none',
        subscription_seat_active: response.subscription_seat_active !== false
      };

      this._aiModelOptions = Array.isArray(response.ai_model_options)
        ? response.ai_model_options
        : [];
      this._processComparisonEnabled = response.process_comparison_enabled === true;
    }
  }

  /**
   * Store external access token (for URL-based authentication)
   */
  setExternalAccessToken(accessToken: string, expiresIn: number = 86400): void {
    this.accessToken = accessToken;
    // Calculate expiry time with 30-second buffer for network delays
    this.tokenExpiry = Date.now() + (expiresIn - 30) * 1000;
  }

  /**
   * Takes the token and profile the cabinet handed to this embedded editor.
   * The cabinet alone refreshes tokens; the refresh cookie is never used here.
   */
  applyEmbeddedGrant(payload: EmbeddedAuthPayload): LoginResponse {
    const response: LoginResponse = {
      ...payload.profile,
      access_token: payload.token,
      expires_in: Math.max(60, Math.round((payload.expiresAt - Date.now()) / 1000)),
    };
    this.storeTokens(response);
    return response;
  }

  /**
   * Initialize authentication and session from URL parameters
   * This handles external shares, legacy URLs with access tokens, and session parameters
   */
  initializeFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);

    // Handle authentication tokens
    const accessToken = urlParams.get('access_token');
    if (accessToken) {
      // This is likely an external share or legacy URL - store token securely
      this.setExternalAccessToken(accessToken, 86400); // 24 hours default
    }

    // Handle session parameters
    this.sessionService.other_user_id = urlParams.get('other_user_id');
    let projectId = window.location.pathname.split('/')[1];
    if (projectId === 'mold' || projectId === 'editor' || projectId === 'jaws') {
      projectId = window.location.pathname.split('/')[2];
    }
    this.sessionService.project_id = projectId;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.accessToken !== null && this.tokenExpiry !== null && Date.now() < this.tokenExpiry;
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    if (this.tokenExpiry && Date.now() > this.tokenExpiry) {
      this.clearTokens();
      return null;
    }
    return this.accessToken;
  }

  /**
   * Get email
   */
  getEmail(): string | null {
    return this.email;
  }

  isAdmin(): boolean {
    return this.userRole === 'admin';
  }

  /** Process comparison is offered only where the backend enables it; users still opt in. */
  processComparisonEnabled(): boolean {
    return this._processComparisonEnabled;
  }

  /**
   * Clear all tokens and subscription features
   */
  clearTokens(): void {
    this.accessToken = null;
    this.tokenExpiry = null;
    this.email = null;
    this.userRole = null;
    this._subscriptionFeatures = {
      manufacturing_features: [],
      cad_generation_features: [],
      platform_features: [],
      load_case_features: [],
      constraints_features: [],
      cad_download_features: [],
      plugin_features: [],
      push_pull_features: {
        organic: false,
        prismatic: false,
        native_parametric: false
      },
      subscription_status: 'none',
      subscription_seat_active: true
    };
    this._aiModelOptions = [];
    this._processComparisonEnabled = false;
  }

  /**
   * Check if subscriptions are enabled
   */
  get subscriptionsEnabled(): boolean {
    return environment.SUBSCRIPTIONS_ENABLED === true;
  }

  /**
   * Get effective subscription features
   * - If subscriptions disabled: grant all features
   * - If debug override set: use debug override
   * - Otherwise: use actual subscription features from token
   */
  private getEffectiveFeatures(): SubscriptionFeatures {
    // If subscriptions are disabled, grant all features
    if (!environment.SUBSCRIPTIONS_ENABLED) {
      return ALL_FEATURES;
    }

    // If debug override is set (and debug mode is enabled), use it
    if (environment.SUBSCRIPTION_DEBUG && this._debugOverride) {
      return this._debugOverride;
    }

    // Use actual subscription features from token
    return this._subscriptionFeatures;
  }

  /**
   * Get subscription features
   */
  get subscriptionFeatures(): SubscriptionFeatures {
    return this.getEffectiveFeatures();
  }

  /**
   * Get subscription status
   */
  get subscriptionStatus(): string {
    return this.getEffectiveFeatures().subscription_status;
  }

  /**
   * Check if subscription is active
   */
  isSubscriptionActive(): boolean {
    return this.getEffectiveFeatures().subscription_status === 'active';
  }

  /**
   * Check if user has restricted access (expired subscription, seat inactive, etc.)
   * Used to disable create/update/delete actions throughout the editor
   */
  hasRestrictedAccess(): boolean {
    // If subscriptions are disabled, no restrictions apply
    if (!environment.SUBSCRIPTIONS_ENABLED) {
      return false;
    }
    // Shared users are not subject to subscription restrictions
    // (the backend uses the share creator's subscription for access control)
    if (this.sessionService.sharing) {
      return false;
    }
    // Restricted access when subscription is not active or seat is inactive
    return !this.isSubscriptionActive() || this.getEffectiveFeatures().subscription_seat_active === false;
  }

  /**
   * Check if user's subscription seat is inactive
   */
  hasInactiveSeat(): boolean {
    return this.getEffectiveFeatures().subscription_seat_active === false;
  }

  /**
   * Get the reason for restricted access (for displaying appropriate message)
   */
  getRestrictedAccessReason(): 'expired' | 'none' | 'processing' | 'seat_inactive' | null {
    if (!environment.SUBSCRIPTIONS_ENABLED) {
      return null;
    }
    if (this.getEffectiveFeatures().subscription_seat_active === false) {
      return 'seat_inactive';
    }
    if (!this.isSubscriptionActive()) {
      const status = this.getEffectiveFeatures().subscription_status;
      if (status === 'none') {
        return 'none';
      }
      if (status === 'processing') {
        return 'processing';
      }
      return 'expired';
    }
    return null;
  }

  /**
   * Check if a manufacturing feature is available
   */
  hasManufacturingFeature(feature: string): boolean {
    return this.getEffectiveFeatures().manufacturing_features.includes(feature);
  }

  /**
   * Check if a CAD generation feature is available
   */
  hasCadFeature(feature: string): boolean {
    return this.getEffectiveFeatures().cad_generation_features.includes(feature);
  }

  /**
   * Check if a platform feature is available
   */
  hasPlatformFeature(feature: string): boolean {
    return this.getEffectiveFeatures().platform_features.includes(feature);
  }

  /**
   * Check if AI Chat feature is available
   */
  hasAiChatFeature(): boolean {
    return this.hasPlatformFeature(PlatformFeatures.AI_CHAT);
  }

  /**
   * Copilot models this user may pick from.
   *
   * Never empty — an older backend (or the desktop build, which has no login)
   * sends nothing, and the standard model is always available. Eligibility is
   * the server's call: the WebSocket gate re-checks every key it is sent, so
   * editing this list in the browser buys nothing but an error frame.
   */
  getAiModelOptions(): AiModelOption[] {
    return this._aiModelOptions.length ? this._aiModelOptions : [DEFAULT_AI_MODEL_OPTION];
  }

  /** Is *key* one this user may actually use? */
  canUseAiModel(key: string): boolean {
    return this.getAiModelOptions().some((option) => option.key === key);
  }

  /**
   * Get all available manufacturing features
   */
  getManufacturingFeatures(): string[] {
    return this.getEffectiveFeatures().manufacturing_features;
  }

  /**
   * Get all available CAD generation features
   */
  getCadFeatures(): string[] {
    return this.getEffectiveFeatures().cad_generation_features;
  }

  /**
   * Get all available platform features
   */
  getPlatformFeatures(): string[] {
    return this.getEffectiveFeatures().platform_features;
  }

  /**
   * Check if a load case feature is available
   */
  hasLoadCaseFeature(feature: string): boolean {
    return this.getEffectiveFeatures().load_case_features.includes(feature);
  }

  /**
   * Get all available load case features
   */
  getLoadCaseFeatures(): string[] {
    return this.getEffectiveFeatures().load_case_features;
  }

  /**
   * Check if a constraint feature is available
   */
  hasConstraintFeature(feature: string): boolean {
    return this.getEffectiveFeatures().constraints_features.includes(feature);
  }

  /**
   * Get all available constraint features
   */
  getConstraintFeatures(): string[] {
    return this.getEffectiveFeatures().constraints_features;
  }

  /**
   * Check if a manufacturing method (by enum value) is allowed
   */
  isManufacturingMethodAllowed(manufacturingMethod: number): boolean {
    const feature = MANUFACTURING_METHOD_TO_FEATURE[manufacturingMethod];
    if (!feature) return true; // Unknown method - allow by default

    return this.hasManufacturingFeature(feature);
  }

  /**
   * Check if prismatic CAD generation is available
   * Currently disabled - prismatic generation is hidden from UI
   */
  canGeneratePrismatic(): boolean {
    // return this.hasCadFeature(CadGenerationFeatures.PRISMATIC);
    return false;
  }

  /**
   * Check if organic CAD generation is available
   */
  canGenerateOrganic(): boolean {
    return this.hasCadFeature(CadGenerationFeatures.ORGANIC);
  }

  /**
   * Check if a CAD download feature is available
   */
  hasCadDownloadFeature(feature: string): boolean {
    return this.getEffectiveFeatures().cad_download_features.includes(feature);
  }

  /**
   * Get all available CAD download features
   */
  getCadDownloadFeatures(): string[] {
    return this.getEffectiveFeatures().cad_download_features;
  }

  /**
   * Check if organic CAD download is available
   */
  canDownloadOrganic(): boolean {
    return this.hasCadDownloadFeature(CadDownloadFeatures.ORGANIC);
  }

  /**
   * Check if prismatic CAD download is available
   */
  canDownloadPrismatic(): boolean {
    return this.hasCadDownloadFeature(CadDownloadFeatures.PRISMATIC);
  }

  // === Debug Methods (only work when SUBSCRIPTION_DEBUG is enabled) ===

  /**
   * Check if subscription debug mode is enabled
   */
  get isDebugEnabled(): boolean {
    return environment.SUBSCRIPTION_DEBUG === true;
  }

  /**
   * Set debug override for subscription features
   */
  setDebugOverride(features: SubscriptionFeatures): void {
    if (!environment.SUBSCRIPTION_DEBUG) return;
    this._debugOverride = features;
  }

  /**
   * Clear debug override (use real subscription features)
   */
  clearDebugOverride(): void {
    this._debugOverride = null;
  }

  /**
   * Get the actual subscription features (without debug override)
   */
  getActualFeatures(): SubscriptionFeatures {
    return this._subscriptionFeatures;
  }

  /**
   * Get current debug override (if any)
   */
  getDebugOverride(): SubscriptionFeatures | null {
    return this._debugOverride;
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<any> {
    // If there's already a refresh token request in progress, return the cached observable
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    // Embedded editors never call /auth/refresh: it would revoke the cabinet's refresh token.
    if (this.embeddedBridge.isEmbedded) {
      this.refreshToken$ = this.embeddedBridge.requestAuth(EMBEDDED_AUTH_TIMEOUT_MS).pipe(
        map((payload) => this.applyEmbeddedGrant(payload)),
        catchError((error) => {
          this.clearTokens();
          return throwError(() => error);
        }),
        tap({
          next: () => {
            this.refreshToken$ = null;
          },
          error: () => {
            this.refreshToken$ = null;
          }
        }),
        shareReplay(1)
      );
      return this.refreshToken$;
    }

    // Create new refresh token request and cache it
    this.refreshToken$ = this.http
      .post<any>(
        `${environment.API_ADDRESS}/auth/refresh`,
        {},
        {
          context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
          withCredentials: true // Important: Include cookies in request
        }
      )
      .pipe(
        map((response) => {
          this.storeTokens(response);
          return response;
        }),
        catchError((error) => {
          this.clearTokens();
          return throwError(() => error);
        }),
        tap({
          next: () => {
            // Clear the cached observable after successful completion
            this.refreshToken$ = null;
          },
          error: () => {
            // Clear the cached observable after error
            this.refreshToken$ = null;
          }
        }),
        shareReplay(1) // Share the result with multiple subscribers
      );

    return this.refreshToken$;
  }

  /**
   * Get remaining token lifetime in seconds
   */
  getTokenRemainingTime(): number {
    if (!this.tokenExpiry) return 0;
    const remaining = Math.max(0, Math.floor((this.tokenExpiry - Date.now()) / 1000));
    return remaining;
  }

  /**
   * Check if token will expire soon (within 5 minutes)
   */
  isTokenExpiringSoon(): boolean {
    if (!this.tokenExpiry) return false;
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    return this.tokenExpiry - Date.now() < fiveMinutes;
  }

  /**
   * Get frontend address
   */
  getFrontendAddress(): string {
    return environment.FRONTEND_ADDRESS;
  }

  /**
   * Logout
   */
  logout(): void {
    this.clearTokens();
    if (this.embeddedBridge.isEmbedded) {
      this.embeddedBridge.navigateHost('/login');
      return;
    }
    // Redirect to frontend login
    window.location.href = `${environment.FRONTEND_ADDRESS}/login`;
  }
}
